import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaTrophy, FaUsers } from 'react-icons/fa';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightBg: '#f8fafc' };

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
`;

const PageSubtitle = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0 0 1.5rem 0;
  max-width: 520px;
`;

const Card = styled(motion.div)`
  background: #fff;
  border-radius: 16px;
  padding: 2rem 2rem 2.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(4, 54, 99, 0.06);
  border: 1px solid rgba(4, 54, 99, 0.08);
  text-align: center;
`;

const IconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: linear-gradient(135deg, ${BRAND.red} 0%, #b91c1c 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  margin: 0 auto 1.25rem;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
`;

const CardText = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0 0 1.25rem 0;
  line-height: 1.55;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
`;

const Hint = styled.p`
  font-size: 0.8125rem;
  color: #94a3b8;
  margin: 0 0 1.5rem 0;
`;

const CtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  background: ${BRAND.blue};
  color: #fff;
  font-weight: 600;
  font-size: 0.9375rem;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s;
  &:hover {
    background: #062a4d;
    color: #fff;
    transform: translateY(-1px);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${BRAND.gray};
  margin: 1.5rem 0;
`;

const TeamNote = styled.p`
  font-size: 0.8125rem;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
`;

export default function PlayerRankPage() {
  const { t } = useTranslation();

  return (
    <>
      <PageTitle>{t('playerRankPage.title')}</PageTitle>
      <PageSubtitle>
        <Trans i18nKey="playerRankPage.subtitle" components={{ strong: <strong /> }} />
      </PageSubtitle>

      <Card
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <IconWrap><FaTrophy /></IconWrap>
        <CardTitle>{t('playerRankPage.cardTitle')}</CardTitle>
        <CardText>{t('playerRankPage.cardText')}</CardText>
        <Hint>{t('playerRankPage.hint')}</Hint>
        <CtaLink to="/player/find-match">
          <FaUsers size={14} /> {t('playerRankPage.ctaFindMatch')}
        </CtaLink>

        <Divider />
        <TeamNote>
          <Trans i18nKey="playerRankPage.teamNote" components={{ strong: <strong /> }} />
        </TeamNote>
      </Card>
    </>
  );
}
